import { useCallback, useEffect, useState, useRef } from "react";

/**
 * Hook for PDV keyboard navigation and shortcuts
 *
 * Keyboard shortcuts:
 * - F1: Show help
 * - F2: Focus search (same as Ctrl+B)
 * - F3: Toggle cart
 * - F4: Finalize sale
 * - F10: Confirm payment (same as F4)
 * - F12: Cancel operation
 * - Ctrl+B: Focus search
 * - Ctrl+L: Clear sale
 * - Ctrl+O: Save quote
 * - Ctrl+F: Toggle cart (alternative to F3)
 * - Arrow Up/Down: Navigate products, cart items, or suggestions
 * - Arrow Left/Right: Navigate payment methods
 * - Enter: Add selected product / Confirm action
 * - Delete: Remove selected item from cart
 * - Escape: Cancel / Close drawer
 * - Tab: Navigate between sections
 * - +/-: Increase/decrease quantity
 * - 1-9: Quick quantity set
 */

const SECTIONS = {
  SEARCH: "search",
  PRODUCTS: "products",
  SUGGESTIONS: "suggestions",
  CART: "cart",
  PAYMENT: "payment",
};

const usePdvKeyboard = ({
  products = [],
  cart = [],
  suggestions = [],
  paymentMethods = [],
  onAddToCart,
  onRemoveFromCart,
  onUpdateQuantity,
  onClearCart,
  onSelectPayment,
  onConfirmPayment,
  onCancel,
  onToggleCart,
  onShowHelp,
  onSaveQuote,
  onAddSuggestion,
  enabled = true,
  searchInputRef,
  quantityInputRef,
  salesDrawerOpen = false,
}) => {
  const [activeSection, setActiveSection] = useState(SECTIONS.SEARCH);
  const [selectedProductIndex, setSelectedProductIndex] = useState(-1);
  const [selectedCartIndex, setSelectedCartIndex] = useState(-1);
  const [selectedPaymentIndex, setSelectedPaymentIndex] = useState(0);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [shortcutsEnabled, setShortcutsEnabled] = useState(true);

  const lastKeyTime = useRef(0);
  const quantityBuffer = useRef("");
  const quantityTimeout = useRef(null);

  // Reset all indices
  const resetIndices = useCallback(() => {
    setSelectedProductIndex(-1);
    setSelectedCartIndex(-1);
    setSelectedSuggestionIndex(-1);
    setActiveSection(SECTIONS.SEARCH);
  }, []);

  // Reset product selection when products change
  useEffect(() => {
    if (products.length > 0 && selectedProductIndex === -1) {
      setSelectedProductIndex(0);
    } else if (products.length === 0) {
      setSelectedProductIndex(-1);
    } else if (selectedProductIndex >= products.length) {
      setSelectedProductIndex(products.length - 1);
    }
  }, [products, selectedProductIndex]);

  // Reset cart selection when cart changes
  useEffect(() => {
    if (cart.length > 0 && selectedCartIndex === -1) {
      setSelectedCartIndex(0);
    } else if (cart.length === 0) {
      setSelectedCartIndex(-1);
    } else if (selectedCartIndex >= cart.length) {
      setSelectedCartIndex(cart.length - 1);
    }
  }, [cart, selectedCartIndex]);

  // Reset suggestion selection when suggestions change
  useEffect(() => {
    if (suggestions.length > 0 && selectedSuggestionIndex === -1) {
      setSelectedSuggestionIndex(0);
    } else if (suggestions.length === 0) {
      setSelectedSuggestionIndex(-1);
    } else if (selectedSuggestionIndex >= suggestions.length) {
      setSelectedSuggestionIndex(suggestions.length - 1);
    }
  }, [suggestions, selectedSuggestionIndex]);

  // Focus cart section when drawer opens
  useEffect(() => {
    if (salesDrawerOpen && cart.length > 0) {
      setActiveSection(SECTIONS.CART);
      if (selectedCartIndex === -1) {
        setSelectedCartIndex(0);
      }
    }
  }, [salesDrawerOpen, cart.length, selectedCartIndex]);

  // Disable shortcuts when typing in inputs
  const disableShortcuts = useCallback(() => {
    setShortcutsEnabled(false);
  }, []);

  const enableShortcuts = useCallback(() => {
    setShortcutsEnabled(true);
  }, []);

  // Focus search input
  const focusSearch = useCallback(() => {
    setActiveSection(SECTIONS.SEARCH);
    searchInputRef?.current?.focus();
    searchInputRef?.current?.select();
  }, [searchInputRef]);

  // Navigate to products section
  const focusProducts = useCallback(() => {
    setActiveSection(SECTIONS.PRODUCTS);
    if (products.length > 0 && selectedProductIndex === -1) {
      setSelectedProductIndex(0);
    }
  }, [products.length, selectedProductIndex]);

  // Navigate to suggestions section
  const focusSuggestions = useCallback(() => {
    if (suggestions.length > 0) {
      setActiveSection(SECTIONS.SUGGESTIONS);
      if (selectedSuggestionIndex === -1) {
        setSelectedSuggestionIndex(0);
      }
    }
  }, [suggestions.length, selectedSuggestionIndex]);

  // Navigate to cart section
  const focusCart = useCallback(() => {
    setActiveSection(SECTIONS.CART);
    if (cart.length > 0 && selectedCartIndex === -1) {
      setSelectedCartIndex(0);
    }
  }, [cart.length, selectedCartIndex]);

  // Navigate to payment section
  const focusPayment = useCallback(() => {
    setActiveSection(SECTIONS.PAYMENT);
  }, []);

  // Navigate between sections with Tab
  const navigateSection = useCallback((reverse = false) => {
    // Build available sections based on content
    const availableSections = [SECTIONS.SEARCH];
    if (products.length > 0) availableSections.push(SECTIONS.PRODUCTS);
    if (suggestions.length > 0) availableSections.push(SECTIONS.SUGGESTIONS);
    if (salesDrawerOpen && cart.length > 0) {
      availableSections.push(SECTIONS.CART);
      availableSections.push(SECTIONS.PAYMENT);
    }

    const currentIndex = availableSections.indexOf(activeSection);
    let nextIndex;

    if (currentIndex === -1) {
      nextIndex = 0;
    } else if (reverse) {
      nextIndex = currentIndex <= 0 ? availableSections.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex >= availableSections.length - 1 ? 0 : currentIndex + 1;
    }

    const nextSection = availableSections[nextIndex];
    setActiveSection(nextSection);

    switch (nextSection) {
      case SECTIONS.SEARCH:
        focusSearch();
        break;
      case SECTIONS.PRODUCTS:
        focusProducts();
        break;
      case SECTIONS.SUGGESTIONS:
        focusSuggestions();
        break;
      case SECTIONS.CART:
        focusCart();
        break;
      case SECTIONS.PAYMENT:
        focusPayment();
        break;
      default:
        break;
    }
  }, [activeSection, products.length, suggestions.length, cart.length, salesDrawerOpen, focusSearch, focusProducts, focusSuggestions, focusCart, focusPayment]);

  // Navigate within current section
  const navigateUp = useCallback(() => {
    switch (activeSection) {
      case SECTIONS.PRODUCTS:
        setSelectedProductIndex(prev => Math.max(0, prev - 1));
        break;
      case SECTIONS.SUGGESTIONS:
        setSelectedSuggestionIndex(prev => Math.max(0, prev - 1));
        break;
      case SECTIONS.CART:
        setSelectedCartIndex(prev => Math.max(0, prev - 1));
        break;
      case SECTIONS.PAYMENT:
        setSelectedPaymentIndex(prev => Math.max(0, prev - 1));
        break;
      default:
        break;
    }
  }, [activeSection]);

  const navigateDown = useCallback(() => {
    switch (activeSection) {
      case SECTIONS.PRODUCTS:
        setSelectedProductIndex(prev => Math.min(products.length - 1, prev + 1));
        break;
      case SECTIONS.SUGGESTIONS:
        setSelectedSuggestionIndex(prev => Math.min(suggestions.length - 1, prev + 1));
        break;
      case SECTIONS.CART:
        setSelectedCartIndex(prev => Math.min(cart.length - 1, prev + 1));
        break;
      case SECTIONS.PAYMENT:
        setSelectedPaymentIndex(prev => Math.min(paymentMethods.length - 1, prev + 1));
        break;
      default:
        break;
    }
  }, [activeSection, products.length, suggestions.length, cart.length, paymentMethods.length]);

  // Handle quantity input via number keys
  const handleQuantityInput = useCallback((digit) => {
    if (quantityTimeout.current) {
      clearTimeout(quantityTimeout.current);
    }

    quantityBuffer.current += digit;

    quantityTimeout.current = setTimeout(() => {
      const quantity = parseInt(quantityBuffer.current, 10);
      quantityBuffer.current = "";

      if (quantity > 0 && quantity <= 999) {
        if (activeSection === SECTIONS.PRODUCTS && selectedProductIndex >= 0) {
          const product = products[selectedProductIndex];
          if (product) {
            onAddToCart?.(product, quantity);
          }
        } else if (activeSection === SECTIONS.CART && selectedCartIndex >= 0) {
          const item = cart[selectedCartIndex];
          if (item) {
            onUpdateQuantity?.(item.id, quantity - item.quantity);
          }
        }
      }
    }, 500);
  }, [activeSection, selectedProductIndex, selectedCartIndex, products, cart, onAddToCart, onUpdateQuantity]);

  // Handle Enter key
  const handleEnter = useCallback(() => {
    switch (activeSection) {
      case SECTIONS.PRODUCTS:
        if (selectedProductIndex >= 0 && products[selectedProductIndex]) {
          onAddToCart?.(products[selectedProductIndex]);
        }
        break;
      case SECTIONS.SUGGESTIONS:
        if (selectedSuggestionIndex >= 0 && suggestions[selectedSuggestionIndex]) {
          onAddSuggestion?.(suggestions[selectedSuggestionIndex]);
        }
        break;
      case SECTIONS.CART:
        // Move to payment
        focusPayment();
        break;
      case SECTIONS.PAYMENT:
        onConfirmPayment?.();
        break;
      default:
        break;
    }
  }, [activeSection, selectedProductIndex, selectedSuggestionIndex, products, suggestions, onAddToCart, onAddSuggestion, onConfirmPayment, focusPayment]);

  // Handle Delete key
  const handleDelete = useCallback(() => {
    if (activeSection === SECTIONS.CART && selectedCartIndex >= 0 && cart[selectedCartIndex]) {
      onRemoveFromCart?.(cart[selectedCartIndex].id);
    }
  }, [activeSection, selectedCartIndex, cart, onRemoveFromCart]);

  // Increase quantity
  const increaseQuantity = useCallback(() => {
    if (activeSection === SECTIONS.CART && selectedCartIndex >= 0 && cart[selectedCartIndex]) {
      onUpdateQuantity?.(cart[selectedCartIndex].id, 1);
    }
  }, [activeSection, selectedCartIndex, cart, onUpdateQuantity]);

  // Decrease quantity
  const decreaseQuantity = useCallback(() => {
    if (activeSection === SECTIONS.CART && selectedCartIndex >= 0 && cart[selectedCartIndex]) {
      onUpdateQuantity?.(cart[selectedCartIndex].id, -1);
    }
  }, [activeSection, selectedCartIndex, cart, onUpdateQuantity]);

  // Main keyboard handler
  const handleKeyDown = useCallback((event) => {
    if (!enabled) return;

    // Guard against undefined key (can happen with some browser events like paste)
    if (!event.key) return;

    // Check if we're in an input field
    const isInInput = ["INPUT", "TEXTAREA"].includes(event.target?.tagName);
    const isFunctionKey = event.key.startsWith("F") && event.key.length <= 3;
    const isNavigationKey = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Enter", "Escape"].includes(event.key);
    const isCtrlKey = event.ctrlKey || event.metaKey;

    // Always allow Ctrl+ shortcuts
    if (isCtrlKey) {
      switch (event.key.toLowerCase()) {
        case "b":
          event.preventDefault();
          focusSearch();
          return;
        case "l":
          event.preventDefault();
          onClearCart?.();
          return;
        case "o":
          event.preventDefault();
          onSaveQuote?.();
          return;
        case "f":
          event.preventDefault();
          onToggleCart?.();
          return;
        default:
          break;
      }
    }

    // Allow function keys and navigation keys even in inputs
    if (!isFunctionKey && !isNavigationKey && isInInput && !shortcutsEnabled) {
      return;
    }

    // Prevent rapid key repeats
    const now = Date.now();
    if (now - lastKeyTime.current < 50) {
      return;
    }
    lastKeyTime.current = now;

    switch (event.key) {
      // Function keys
      case "F1":
        event.preventDefault();
        onShowHelp?.();
        break;

      case "F2":
        event.preventDefault();
        focusSearch();
        break;

      case "F3":
        event.preventDefault();
        onToggleCart?.();
        break;

      case "F4":
        event.preventDefault();
        onConfirmPayment?.();
        break;

      case "F5":
        event.preventDefault();
        focusPayment();
        break;

      case "F6":
        event.preventDefault();
        quantityInputRef?.current?.focus();
        quantityInputRef?.current?.select();
        break;

      case "F10":
        event.preventDefault();
        onConfirmPayment?.();
        break;

      case "F12":
        event.preventDefault();
        onCancel?.();
        break;

      // Navigation - Arrow keys work even in search input to navigate products
      case "ArrowUp":
        event.preventDefault();
        // If in search and have products, navigate products
        if (isInInput && activeSection === SECTIONS.SEARCH && products.length > 0) {
          setActiveSection(SECTIONS.PRODUCTS);
          setSelectedProductIndex(prev => Math.max(0, prev - 1));
        } else if (!isInInput) {
          navigateUp();
        }
        break;

      case "ArrowDown":
        event.preventDefault();
        // If in search and have products, navigate products
        if (isInInput && activeSection === SECTIONS.SEARCH && products.length > 0) {
          setActiveSection(SECTIONS.PRODUCTS);
          setSelectedProductIndex(prev => Math.min(products.length - 1, prev + 1));
        } else if (!isInInput) {
          navigateDown();
        }
        break;

      case "ArrowLeft":
        if (activeSection === SECTIONS.PAYMENT) {
          event.preventDefault();
          setSelectedPaymentIndex(prev => Math.max(0, prev - 1));
        } else if (activeSection === SECTIONS.SUGGESTIONS) {
          event.preventDefault();
          setSelectedSuggestionIndex(prev => Math.max(0, prev - 1));
        }
        break;

      case "ArrowRight":
        if (activeSection === SECTIONS.PAYMENT) {
          event.preventDefault();
          setSelectedPaymentIndex(prev => Math.min(paymentMethods.length - 1, prev + 1));
        } else if (activeSection === SECTIONS.SUGGESTIONS) {
          event.preventDefault();
          setSelectedSuggestionIndex(prev => Math.min(suggestions.length - 1, prev + 1));
        }
        break;

      case "Tab":
        if (!isInInput || event.shiftKey) {
          event.preventDefault();
          navigateSection(event.shiftKey);
        }
        break;

      // Actions
      case "Enter":
        if (!isInInput || activeSection === SECTIONS.SEARCH) {
          if (activeSection !== SECTIONS.SEARCH) {
            event.preventDefault();
          }
          handleEnter();
        }
        break;

      case "Delete":
        if (!isInInput) {
          event.preventDefault();
          handleDelete();
        }
        break;

      case "Escape":
        event.preventDefault();
        if (salesDrawerOpen) {
          onToggleCart?.();
        } else if (activeSection === SECTIONS.PAYMENT) {
          focusCart();
        } else {
          onCancel?.();
        }
        break;

      // Quantity controls
      case "+":
      case "=":
        if (!isInInput) {
          event.preventDefault();
          increaseQuantity();
        }
        break;

      case "-":
        if (!isInInput) {
          event.preventDefault();
          decreaseQuantity();
        }
        break;

      // Number keys for quantity
      case "0":
      case "1":
      case "2":
      case "3":
      case "4":
      case "5":
      case "6":
      case "7":
      case "8":
      case "9":
        if (!isInInput && (activeSection === SECTIONS.PRODUCTS || activeSection === SECTIONS.CART)) {
          event.preventDefault();
          handleQuantityInput(event.key);
        }
        break;

      default:
        break;
    }
  }, [
    enabled,
    shortcutsEnabled,
    activeSection,
    products.length,
    suggestions.length,
    paymentMethods.length,
    salesDrawerOpen,
    focusSearch,
    focusPayment,
    focusCart,
    navigateUp,
    navigateDown,
    navigateSection,
    handleEnter,
    handleDelete,
    increaseQuantity,
    decreaseQuantity,
    handleQuantityInput,
    onShowHelp,
    onToggleCart,
    onClearCart,
    onSaveQuote,
    onConfirmPayment,
    onCancel,
    quantityInputRef,
  ]);

  // Attach keyboard listener
  useEffect(() => {
    if (enabled) {
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
        if (quantityTimeout.current) {
          clearTimeout(quantityTimeout.current);
        }
      };
    }
  }, [enabled, handleKeyDown]);

  return {
    activeSection,
    setActiveSection,
    selectedProductIndex,
    setSelectedProductIndex,
    selectedCartIndex,
    setSelectedCartIndex,
    selectedPaymentIndex,
    setSelectedPaymentIndex,
    selectedSuggestionIndex,
    setSelectedSuggestionIndex,
    disableShortcuts,
    enableShortcuts,
    focusSearch,
    focusProducts,
    focusSuggestions,
    focusCart,
    focusPayment,
    resetIndices,
    SECTIONS,
  };
};

export default usePdvKeyboard;
